import React from 'react';
import { Printer, Save, Share2, AlertTriangle } from 'lucide-react';
import { NavigationDropdown } from './NavigationDropdown';
export type AggregatedIngredient = {
  item: string;
  description: string;
  thumbnail: string;
  totalAmount: number;
  unit: string;
  price: string;
  location: string;
  inStock: boolean;
  usedInRecipes: string[];
};
interface StoreShoppingListProps {
  store: 'kroger' | 'meijer' | 'walmart';
  ingredients: AggregatedIngredient[];
  onBack: () => void;
  onNavigateHome: () => void;
  onNavigateLanding?: () => void;
  onGenerateRecipe: () => void;
  onImportRecipe: () => void;
  onBrowseRecipes: () => void;
  onManageGuests: () => void;
  navigationContext?: 'home' | 'browse';
}
const STORE_NAMES = {
  kroger: 'Kroger',
  meijer: 'Meijer',
  walmart: 'Walmart'
};
export function StoreShoppingList({
  store,
  ingredients,
  onBack,
  onNavigateHome,
  onNavigateLanding,
  onGenerateRecipe,
  onImportRecipe,
  onBrowseRecipes,
  onManageGuests,
  navigationContext = 'home'
}: StoreShoppingListProps) {
  const totalCost = ingredients.reduce((sum, ing) => ing.inStock ? sum + parseFloat(ing.price.replace('$', '')) : sum, 0);
  const outOfStockCount = ingredients.filter(i => !i.inStock).length;
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933]" data-id="element-708">
      {/* Header */}
      <header className="bg-white border-b-2 border-gray-200 sticky top-0 z-10" data-id="element-709">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4" data-id="element-710">
          <div className="flex flex-col gap-4 mb-4" data-id="element-711">
            {/* Top row: Navigation and action buttons */}
            <div className="flex items-center justify-between gap-4" data-id="element-712">
              <NavigationDropdown onNavigateHome={onNavigateHome} onNavigateLanding={onNavigateLanding} onGenerateRecipe={onGenerateRecipe} onImportRecipe={onImportRecipe} onBrowseRecipes={onBrowseRecipes} onManageGuests={onManageGuests} defaultAction={navigationContext} data-id="element-713" />

              <div className="flex items-center gap-2" data-id="element-714">
                <button className="flex items-center space-x-2 px-3 py-2 border border-gray-300 hover:border-gray-500 transition-colors bg-white" data-id="element-715">
                  <Printer className="w-4 h-4 text-gray-600" data-id="element-716" />
                  <span className="font-mono text-xs text-gray-600 hidden sm:inline" data-id="element-717">
                    Print
                  </span>
                </button>
                <button className="flex items-center space-x-2 px-3 py-2 border border-gray-300 hover:border-gray-500 transition-colors bg-white" data-id="element-718">
                  <Save className="w-4 h-4 text-gray-600" data-id="element-719" />
                  <span className="font-mono text-xs text-gray-600 hidden sm:inline" data-id="element-720">
                    Save
                  </span>
                </button>
                <button className="flex items-center space-x-2 px-3 py-2 border border-gray-300 hover:border-gray-500 transition-colors bg-white" data-id="element-721">
                  <Share2 className="w-4 h-4 text-gray-600" data-id="element-722" />
                  <span className="font-mono text-xs text-gray-600 hidden sm:inline" data-id="element-723">
                    Share
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div data-id="element-724">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-725">
              {STORE_NAMES[store]} Shopping List
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-2" data-id="element-726">
              <p className="text-sm font-mono text-gray-500" data-id="element-727">
                {ingredients.length} items • Est. Total: ${totalCost.toFixed(2)}
              </p>
              {outOfStockCount > 0 && <span className="px-2 py-1 bg-gray-900 text-white text-xs font-mono" data-id="element-728">
                  {outOfStockCount} OUT OF STOCK
                </span>}
            </div>
          </div>
        </div>
      </header>

      {/* Shopping List */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 space-y-4" data-id="element-729">
        {ingredients.map((ingredient, idx) => <div key={idx} className={`bg-white border-2 transition-colors ${ingredient.inStock ? 'border-gray-300 hover:border-gray-400' : 'border-gray-900 bg-gray-50'}`} data-id="element-730">
            <div className="p-4 md:p-6" data-id="element-731">
              <div className="flex gap-4 md:gap-6" data-id="element-732">
                {/* Thumbnail */}
                <div className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0 border-2 border-gray-300 bg-gray-100 flex items-center justify-center text-4xl md:text-5xl" data-id="element-733">
                  {ingredient.thumbnail}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 space-y-3" data-id="element-734">
                  {/* Title & Stock Status */}
                  <div className="flex items-start justify-between gap-3" data-id="element-735">
                    <div className="flex-1 min-w-0" data-id="element-736">
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 leading-tight" data-id="element-737">
                        {ingredient.item}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2" data-id="element-738">
                        {ingredient.description}
                      </p>
                    </div>

                    {!ingredient.inStock && <div className="flex items-center space-x-2 px-3 py-2 bg-gray-900 text-white flex-shrink-0" data-id="element-739">
                        <AlertTriangle className="w-4 h-4" data-id="element-740" />
                        <span className="font-mono text-xs uppercase tracking-widest whitespace-nowrap" data-id="element-741">
                          Out of Stock
                        </span>
                      </div>}
                  </div>

                  {/* Quantity, Price, Location */}
                  <div className="flex flex-wrap items-center gap-4 md:gap-6" data-id="element-742">
                    <div className="space-y-1" data-id="element-743">
                      <span className="font-mono text-xs uppercase tracking-wider text-gray-500 block" data-id="element-744">
                        Quantity
                      </span>
                      <span className="font-mono text-lg font-bold text-gray-900" data-id="element-745">
                        {ingredient.totalAmount} {ingredient.unit}
                      </span>
                    </div>

                    <div className="space-y-1" data-id="element-746">
                      <span className="font-mono text-xs uppercase tracking-wider text-gray-500 block" data-id="element-747">
                        Price
                      </span>
                      <span className="font-mono text-lg font-bold text-gray-900" data-id="element-748">
                        {ingredient.price}
                      </span>
                    </div>

                    <div className="space-y-1" data-id="element-749">
                      <span className="font-mono text-xs uppercase tracking-wider text-gray-500 block" data-id="element-750">
                        Location
                      </span>
                      <span className="font-mono text-lg font-bold text-gray-900" data-id="element-751">
                        {ingredient.location}
                      </span>
                    </div>
                  </div>

                  {/* Used in Recipes */}
                  {ingredient.usedInRecipes.length > 0 && <div className="pt-2 border-t border-gray-200" data-id="element-752">
                      <span className="font-mono text-xs text-gray-400" data-id="element-753">
                        Used in: {ingredient.usedInRecipes.join(', ')}
                      </span>
                    </div>}
                </div>
              </div>
            </div>
          </div>)}

        {/* Summary Card */}
        <div className="bg-white border-2 border-gray-800 p-6 space-y-4" data-id="element-754">
          <div className="flex justify-between items-center" data-id="element-755">
            <div data-id="element-756">
              <p className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-757">
                Total Items
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-758">
                {ingredients.length}
              </p>
            </div>
            <div className="text-right" data-id="element-759">
              <p className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-760">
                Estimated Total
              </p>
              <p className="text-3xl font-bold text-gray-900" data-id="element-761">
                ${totalCost.toFixed(2)}
              </p>
            </div>
          </div>

          {outOfStockCount > 0 && <div className="pt-4 border-t border-gray-200" data-id="element-762">
              <p className="text-sm text-gray-600" data-id="element-763">
                <strong data-id="element-764">{outOfStockCount}</strong> item
                {outOfStockCount !== 1 ? 's' : ''} currently unavailable.
                Consider substitutes or check back later.
              </p>
            </div>}
        </div>

        {/* Store Info */}
        <div className="p-4 border border-gray-200 bg-white text-xs font-mono text-gray-400 space-y-1" data-id="element-765">
          <p data-id="element-766">
            NOTE: Prices and availability are estimates and may vary by
            location.
          </p>
          <p data-id="element-767">Please verify in-store or online before shopping.</p>
        </div>
      </div>
    </div>;
}